import React from 'react';
import { Country } from '../../utils/ContextProviders/appContextTypes';
import { VerifiedAddress } from '../AddressVerification/addressVerificationTypes';
import { AddressValidationRequest } from '../AddressVerification/utilities/growthConnector';
import { AddressFormState } from './hooks/useHandleFormInput';

export const DEFAULT_COUNTRY_OPTION = <option key="US" value="US">United States</option>;
export const SELECT_COUNTRY_OPTION = <option key="" value="">Select Country</option>;

export const SELECT_COUNTRY_OPTION_STATE = <option key="" value="">Select Country to View States</option>;
export const SELECT_STATE_OPTION = <option key="" value="">Select State</option>;

export const buildCountriesSelect = (countries: Country[]) => {
  if (!countries.length) {
    return ([SELECT_COUNTRY_OPTION, DEFAULT_COUNTRY_OPTION]);
  }
  const countriesSelect = countries.map((country) => {
    const { countryCode, displayName } = country;
    return (<option key={countryCode} value={countryCode}>{displayName}</option>);
  });
  countriesSelect.unshift(SELECT_COUNTRY_OPTION);
  return countriesSelect;
};

export const buildStatesSelect = (countries: Country[], countryCode: string) => {
  const currentCountry = countries.find((country) => (
    country.countryCode.toLowerCase() === countryCode.toLowerCase()));
  if (!currentCountry) {
    return <option key="no_state">Select a Country to Display States</option>;
  }
  if (!currentCountry.hasStates) {
    return <option key="no_state">No State Required</option>;
  }
  const statesSelect = currentCountry.states.map((state) => {
    const { stateCode, displayName } = state;
    return (<option key={stateCode} value={stateCode}>{displayName}</option>);
  });
  statesSelect.unshift(SELECT_STATE_OPTION);
  return statesSelect;
};

export function validateForm(formState: AddressFormState) {
  const {
    shipCountry, shipAddress1, shipCity, shipZipCode,
  } = formState;
  if (
    shipCountry.trim().length === 0
    || shipAddress1.trim().length === 0
    || shipCity.trim().length === 0
    || shipZipCode.trim().length === 0
  ) {
    return false;
  }
  return true;
}

export const shouldSelectBeDisabled = (
  name: string, countries: Country[], currentCountryCode: string,
): boolean => {
  if (name !== 'shipState') {
    return false;
  }
  if (!countries.length) {
    return true;
  }
  if (currentCountryCode === '') {
    return true;
  }
  const currentCountry = countries.find(((c) => c.countryCode === currentCountryCode));
  if (currentCountry) {
    return !currentCountry.hasStates;
  }
  return false;
};

export const mapFormStateToAddresstoValidate = (
  formState: AddressFormState,
): AddressValidationRequest => {
  const {
    shipAddress1, shipAddress2, shipCity, shipCountry, shipState, shipZipCode,
  } = formState;
  return {
    primaryLine: shipAddress1,
    secondaryLine: shipAddress2,
    city: shipCity,
    state: shipState,
    countryCode: shipCountry,
    zip: shipZipCode,
  };
};

export const mapValidatedAddressToFormState = (address: VerifiedAddress): AddressFormState => {
  const {
    primaryLine, secondaryLine, city, state, countryCode, zip,
  } = address;
  return {
    shipAddress1: primaryLine,
    shipAddress2: secondaryLine,
    shipCity: city,
    shipState: state,
    shipCountry: countryCode,
    shipZipCode: zip,
  };
};

export function getCountryNameFromCountryCode(countryCode: string, countries: Country[]): string {
  if (countryCode === 'US') {
    return '';
  }
  const currentCountry = countries.find((country) => (
    country.countryCode.toLowerCase() === countryCode.toLowerCase()));
  if (currentCountry) {
    return currentCountry.displayName;
  }
  return '';
}

export const doesCountryCodeHaveStates = (countryCode: string, countries: Country[]): boolean => {
  const currentCountry = countries.find(((c) => c.countryCode === countryCode));
  if (currentCountry) {
    return currentCountry.hasStates;
  }
  return true;
};
